import style from "../../styles/service-prices.module.scss";
import Calendar from "../calendar";
import Location from "../location";
import { useRef, useState, useEffect } from "react";
import { BottomSpacer } from "../spacer";
import { findEmptyInputFields } from "../../utils/funcs/find-empty-input-ref";
import { validateEmail } from "../../utils/funcs/validate-email";
import { validateMobile } from "../../utils/funcs/validate-mobile";
import SummaryModal from "../modals/summary";
import ContactBanner from "../contact-banner";
import { formatServicePriceWithVAT } from "../../utils/funcs/pricing";

export default function ServiceTemplateComponent({
  backgroundImage,
  item,
  setItem,
  children,
  serviceDescription,
  tomorrow,
  emergency,
  serviceRequired,
}: any) {
  const [finalPrice, setFinalPrice] = useState(item.price);
  const [isValid, setIsValid] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [summaryItem, setSummaryItem] = useState<any>(item);
  const [error, setError] = useState("");
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [access, setAccess] = useState({
    providedByOther: false,
    info: {
      name: item.customerName,
      email: item.customerEmail,
      number: item.customerNumber,
    },
  });
  const [errorMsgs, setErrorMsgs] = useState({
    telError: "",
    nameError: "",
    emailError: "",
    timeError: "",
    address1Error: "",
    postTownError: "",
    postcodeError: "",
    accessNameError: "",
    accessEmailError: "",
    accessNumberError: "",
  });

  const refs = {
    name: useRef(null),
    time: useRef(null),
    email: useRef(null),
    tel: useRef(null),
    address1: useRef(null),
    postTown: useRef(null),
    postcode: useRef(null),
    accessName: useRef(null),
    accessEmail: useRef(null),
    accessNumber: useRef(null),
  };

  useEffect(() => {
    const fetchMaintenanceMode = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL || "http://localhost:5000"}/maintenance-mode`
        );
        if (response.ok) {
          const data = await response.json();
          setMaintenanceMode(data.maintenanceMode);
          setMaintenanceMessage(data.maintenanceMessage);
        }
      } catch (error) {
        console.error("Error fetching maintenance mode:", error);
      }
    };

    fetchMaintenanceMode();
  }, []);

  const handleShowSummary = async (e: any) => {
    e.preventDefault();
    
    const getEmailEndTime = (startTime?: string): string => {
      if (!startTime) return "";
      const normalized = startTime.trim().toUpperCase();
      
      const timeMap: { [key: string]: string } = {
        "08:00 AM": "1:00 PM",
        "8:00 AM": "1:00 PM",
        "1:00 PM": "6:00 PM",
        "13:00": "18:00",
      };
      
      return timeMap[normalized] || "";
    };

    const resolvedEmailEndTime = item?.emailEndTime || getEmailEndTime(item?.emailStartTime);

    const preparedItem = {
      ...item,
      bookingID: item.bookingID?.toString().trim() ? item.bookingID : Date.now(),
      finalPrice,
      emailEndTime: resolvedEmailEndTime,
      address: {
        ...item.address,
      },
    };

    setItem(preparedItem);
    setSummaryItem(preparedItem);
    localStorage.setItem("item", JSON.stringify(preparedItem));
    localStorage.setItem("emailSent", "false");
    const emptyRefs = findEmptyInputFields(refs);

    setErrorMsgs({
      telError: "",
      emailError: "",
      timeError: "",
      address1Error: "",
      postTownError: "",
      postcodeError: "",
      nameError: "",
      accessNameError: "",
      accessEmailError: "",
      accessNumberError: "",
    });

    if (emptyRefs.length > 0) {
      emptyRefs.forEach((ref) => {
        setErrorMsgs((prev: any) => ({
          ...prev,
          [`${ref}Error`]: "Required",
        }));
      });
    }

    if (
      emptyRefs.includes("accessName") ||
      emptyRefs.includes("accessEmail") ||
      emptyRefs.includes("accessNumber")
    ) {
      // Prevent opening the summary modal if access-related fields are empty
      setShowSummary(false);
      setError("Please complete all required fields");
      return;
    }

    const requiredFieldChecks = [
      { label: "name", valid: Boolean(preparedItem?.customerName?.trim()) },
      { label: "email", valid: Boolean(preparedItem?.customerEmail?.trim()) },
      {
        label: "phone number",
        valid: Boolean(preparedItem?.customerNumber?.trim()),
      },
      { label: "appointment date", valid: Boolean(preparedItem?.emailDate) },
      {
        label: "appointment time",
        valid: Boolean(preparedItem?.emailStartTime),
      },
      {
        label: "address line 1",
        valid: Boolean(preparedItem?.address?.line1?.trim()),
      },
      {
        label: "post town",
        valid: Boolean(preparedItem?.address?.postTown?.trim()),
      },
      {
        label: "postcode",
        valid: Boolean(preparedItem?.address?.postcode?.trim()),
      },
    ];

    const firstMissingRequiredField = requiredFieldChecks.find(
      ({ valid }) => !valid
    );

    if (firstMissingRequiredField) {
      setError(
        `Please complete all required fields (missing ${firstMissingRequiredField.label}).`
      );
      return;
    }

    // email and phone validation

    const validBookingEmail = await validateEmail(item.customerEmail);
    const validBookingMobile = await validateMobile(item.customerNumber);

    if (!validBookingEmail) {
      setErrorMsgs((prev: any) => ({ ...prev, emailError: "Invalid email" }));
      setError("Please provide a valid email address");
      return;
    }
    if (!validBookingMobile) {
      setErrorMsgs((prev: any) => ({
        ...prev,
        telError: "Invalid phone number",
      }));
      setError("Please provide a valid phone number");
      return;
    }

    if (access.providedByOther) {
      const validAccessEmail = await validateEmail(access.info.email);
      const validAccessMobile = await validateMobile(access.info.number);
      if (!validAccessEmail) {
        setErrorMsgs((prev: any) => ({
          ...prev,
          accessEmailError: "Invalid email",
        }));
        setError("Please provide a valid email address");
        return;
      }
      if (!validAccessMobile) {
        setErrorMsgs((prev: any) => ({
          ...prev,
          accessNumberError: "Invalid phone number",
        }));
        setError("Please provide a valid phone number");
        return;
      }
    }

    // show booking summary only if all conditions are met
    if (isValid) {
      setShowSummary(true);
      setError("");
    }
  };

  return (
    <>
      <div
        className={style.imgContainer}
        style={{ backgroundImage: `url(${backgroundImage})` }}
      ></div>
      <div className={style.container}>
        <div className={style.intro}>
          <h3 className={style.heading}>{item.productName}</h3>
          <div className={style.description}>{serviceDescription}</div>
          {emergency && (
            <>
              <ContactBanner emergency={emergency} />
              <div className={style.spacer} />
            </>
          )}
          <p className={style.availability}>
            Check out our availability and book the date and time that works for
            you.
          </p>
        </div>
        <form onSubmit={handleShowSummary}>
          {maintenanceMode ? (
            <div className={style.flex}>
              <div style={{ 
                gridColumn: "1 / -1", 
                padding: "2rem", 
                backgroundColor: "#f8f9fa", 
                border: "1px solid #e9ecef", 
                borderRadius: "8px",
                textAlign: "center"
              }}>
                <h3 style={{ fontSize: "1.5rem", marginBottom: "1rem", color: "#d9534f" }}>
                  🔧 Website Maintenance
                </h3>
                <p style={{ fontSize: "1.1rem", color: "#555", lineHeight: "1.6" }}>
                  {maintenanceMessage}
                </p>
              </div>
            </div>
          ) : (
            <div className={style.flex}>
              <div className={style.flexItem1}>
                <h3 className={style.heading}>Service Details</h3>
                <div className={style.divider} />
                {children}
                <Calendar
                  item={item}
                  setItem={setItem}
                  refs={refs}
                  errorMsgs={errorMsgs}
                  setErrorMsgs={setErrorMsgs}
                  selectedDate={tomorrow}
                  emergency={emergency}
                />
              </div>

              <div className={style.flexItem2}>
                <Location
                  item={item}
                  setFinalPrice={setFinalPrice}
                  setItem={setItem}
                  isValid={isValid}
                  setIsValid={setIsValid}
                  errorMsgs={errorMsgs}
                  setErrorMsgs={setErrorMsgs}
                  refs={refs}
                  access={access}
                  setAccess={setAccess}
                />
                {isValid ? (
                  <>
                    <div className={style.list}>
                      <p className={style.priceLabel}>Price:</p>
                      <p className={style.price}>{formatServicePriceWithVAT(finalPrice)}</p>
                    </div>
                    <p style={{ textAlign: "left", fontSize: "0.9em" }}>
                      <em>This is the final price for your selected service (inc VAT).</em>
                    </p>
                  </>
                ) : (
                  <>
                    <div className={style.list}>
                      <p className={style.priceLabel}>Price:</p>
                      <p className={style.price}>{formatServicePriceWithVAT(item.price)}</p>
                    </div>
                    <p style={{ textAlign: "left", fontSize: "0.9em" }}>
                      <em>This is the final price for your selected service (inc VAT).</em>
                    </p>
                  </>
                )}
                <button
                  type="submit"
                  className={style.button}
                >
                  Go to booking summary
                </button>
                {error && <p className="error">{error}</p>}

                {showSummary && (
                  <SummaryModal
                    show={showSummary}
                    finalPrice={finalPrice}
                    handleClose={() => {
                      setShowSummary(false);
                    }}
                    item={summaryItem}
                    setError={setError}
                    serviceRequired={serviceRequired}
                  />
                )}
              </div>
            </div>
          )}
        </form>
      </div>
      <BottomSpacer />
    </>
  );
}
